/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import ComfortCard from './ComFortCard';
import ParameterCard from './ParameterCard';
import Header from './Header';
import Loader from './loader';
import { useData } from '../context/data.context';
import {
  DashboardDataFilter,
  DataWidgetMinMaxAttribute,
  DeviceSummaryAttribute,
  ParametersType,
  ParticleData
} from '../constants/telosair-interfaces';
import { deviceFilter } from '../_helpers/globalHelper';
import { STALE_TEXT_CLASS, lastReportedLabel } from '../_helpers/staleData';

type DataWidgetProps = {
  onClick: (parameter: ParametersType, serialNumber: number) => void;
  dataFilter: DashboardDataFilter | null;
  extendedLabel: string;
  minMaxData: DataWidgetMinMaxAttribute | null;
};

/**
 * DataWidget component for rendering overview of different-different Parameters.
 * @param props - Properties of the page.
 * @returns A React element representing the DataWidget component.
 */
export default function DataWidget(props: DataWidgetProps): ReactElement {
  const { dataFilter, extendedLabel, onClick, minMaxData } = props;
  const {
    parameters,
    groupDataSummary,
    loading,
    dataSummary,
    dataSummaryMeta,
    deviceDataSummary,
    devices,
    groupTypes
  } = useData();
  const navigate = useNavigate();
  const { data, type, stale, asOf } = deviceFilter(
    dataFilter,
    groupDataSummary,
    deviceDataSummary,
    dataSummary,
    devices,
    groupTypes,
    dataSummaryMeta
  );

  return (
    <div className={`p-4 shadow-md relative flex flex-col flex-1 bg-container-500 rounded `}>
      <Header
        actionButton={() => navigate('/settings/parameter?tab=sequence')}
        actionButtonText="Settings"
        sectionName="Data"
        showIcon
      >
        <span className="text-sm font-regular text-gray-400  cursor-default">{extendedLabel}</span>
      </Header>
      {!loading && !data && (
        <div className="text-gray-700 text-center text-xs w-full mt-5">No data to present.</div>
      )}
      {!loading && data && stale && (
        <div className={`mt-2 flex items-center gap-1 text-xs ${STALE_TEXT_CLASS}`}>
          <span className="material-icons-outlined text-sm leading-none">history</span>
          No data in this period. Showing the last known readings · {lastReportedLabel(asOf)}
        </div>
      )}
      {!loading && (
        <div className="flex flex-1 gap-3 pt-3">
          {data && (
            <div
              className={clsx(
                'grid gap-3 flex-1',
                parameters.length >= 11 ? 'grid-cols-4' : 'grid-cols-3'
              )}
            >
              <div className="space-y-3 flex flex-col">
                {parameters
                  ?.slice(0, 2)
                  .map(
                    (eachComfortProperty) =>
                      data && (
                        <ComfortCard
                          type={type}
                          stale={stale}
                          onClick={onClick}
                          minMaxData={minMaxData}
                          key={eachComfortProperty.parameterName}
                          comfortCardProperties={eachComfortProperty}
                          isParameterDataLoading={loading}
                          particleData={data}
                        />
                      )
                  )}
              </div>
              <div className="space-y-3 flex flex-col justify-between">
                {parameters
                  ?.slice(2, 5)
                  .map(
                    (eachParameterProperty) =>
                      data && (
                        <ParameterCard
                          type={type}
                          stale={stale}
                          particleData={data}
                          onClick={onClick}
                          key={eachParameterProperty.parameterName}
                          minMaxData={minMaxData}
                          parameterProperties={eachParameterProperty}
                        />
                      )
                  )}
              </div>
              <div className="space-y-3 flex flex-col">
                {parameters
                  ?.slice(5, 8)
                  .map(
                    (eachParameterProperty) =>
                      data && (
                        <ParameterCard
                          type={type}
                          stale={stale}
                          particleData={data}
                          onClick={onClick}
                          key={eachParameterProperty.parameterName}
                          minMaxData={minMaxData}
                          parameterProperties={eachParameterProperty}
                        />
                      )
                  )}
              </div>
              {parameters.length >= 11 && (
                <div className="space-y-3 flex flex-col">
                  {parameters
                    ?.slice(8, 11)
                    .map(
                      (eachParameterProperty) =>
                        data && (
                          <ParameterCard
                            type={type}
                            stale={stale}
                            particleData={data}
                            onClick={onClick}
                            key={eachParameterProperty.parameterName}
                            minMaxData={minMaxData}
                            parameterProperties={eachParameterProperty}
                          />
                        )
                    )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* {loading ? (
    <Loader />
  ) : (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
      {parameters
        ?.slice(2, 8)
        .map(
          (eachParameterProperty) =>
            data && (
              <ParameterCard
                particleData={data}
                onClick={onClick}
                key={eachParameterProperty.parameterName}
                parameterDataPerDevice={parameterDataPerDevice}
                parameterProperties={eachParameterProperty}
              />
            )
        )}
    </div>
  )} */}
    </div>
  );
}
